import { z } from 'zod';
import { defineForm } from '../types';
import { pickEntraUser } from '../catalogues/entra-user';

/**
 * Create a user in the client's own Entra tenant, with everything the technician would
 * otherwise set by hand afterwards.
 *
 * Every field comes from the attribute catalogue in `../catalogues/entra-user`, so the keys are
 * Graph's property names and the validated payload is the body of the create-then-PATCH
 * that follows. The onboarding half of the generic request form draws on the same
 * catalogue for a smaller subset; the two cannot drift.
 *
 * Only the creation trio is required. Everything else is a box that may be left blank,
 * except the two selects, which open already answered for the common case.
 */

const SECTIONS = {
  account: 'Account',
  role: 'Role',
  contact: 'Contact',
  region: 'Region',
} as const;

const account = pickEntraUser(['userPrincipalName', 'givenName', 'surname'], {
  section: SECTIONS.account,
});
const role = pickEntraUser(
  [
    'jobTitle',
    'department',
    'companyName',
    'streetAddress',
    'city',
    'state',
    'postalCode',
    'country',
    'employeeId',
  ],
  { section: SECTIONS.role },
);
const contact = pickEntraUser(['mobilePhone', 'businessPhones'], { section: SECTIONS.contact });
const region = pickEntraUser(['usageLocation', 'preferredLanguage'], {
  section: SECTIONS.region,
});

export const createEntraUserSchema = z.object({
  ...account.shape,
  ...role.shape,
  ...contact.shape,
  ...region.shape,
});

export type CreateEntraUserInput = z.input<typeof createEntraUserSchema>;
export type CreateEntraUser = z.output<typeof createEntraUserSchema>;

export const createEntraUser = defineForm({
  id: 'create-entra-user',
  version: '2.0.0',
  customers: 'all-customers',
  title: 'Create an Entra User',
  description: 'Create a new user in your Entra tenant. Only the first three fields are required.',
  schema: createEntraUserSchema,
  caseTitle: (payload) => `${payload.givenName} ${payload.surname}`,
  fields: [...account.fields, ...role.fields, ...contact.fields, ...region.fields],
});
